import { useEffect } from "react";
import { Scale, ShieldCheck, Target, Handshake, Landmark, TrendingUp, Flame } from "lucide-react";
import { GoldButton } from "@/components/ds";
import { Spotlight } from "@/components/ds/Spotlight";
import { CorridorMap } from "@/components/ds/CorridorMap";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

const VALUES = [
  { icon: Scale, title: "Integrity — Clarity & Traceability", quote: "Clarity in contracts, traceability in goods in every transaction." },
  { icon: ShieldCheck, title: "Performance — Protect Both Sides", quote: "Advanced compliance: SBLC, DLC, KYC, AML, origin verification, and secure payments." },
  { icon: Target, title: "Operational Excellence", quote: "Precision, flawless logistics, and adherence to global pricing benchmarks." },
  { icon: Handshake, title: "Trust the Relationship", quote: "Fairness, trust, and partnership with suppliers internationally." },
  { icon: Landmark, title: "Security & Compliance", quote: "Strict adherence to U.S., African, and international trade regulations." },
  { icon: TrendingUp, title: "Value Creation", quote: "Win-win structures for sustainable long-term relationships." },
  { icon: Flame, title: "Execution", quote: "We don't sell slides — we close." },
];

const FOOTPRINT = [
  { label: "Headquarters", items: ["Phoenix, Arizona — 300 W. Clarendon Ave, Suite 325"] },
  { label: "Sourcing Corridors", items: ["Rwanda", "Democratic Republic of the Congo", "Senegal & West Africa", "Zambia Copperbelt", "Tanzania (loading)"] },
  { label: "Fulfillment Hubs", items: ["Dubai", "London", "Doha", "Miami", "Zurich", "New York", "Hong Kong"] },
  { label: "Bullion Origins", items: ["Switzerland", "United States", "Hong Kong"] },
];

const About = () => {
  useEffect(() => { document.title = "About — The Zorem Story"; }, []);

  return (
    <div data-testid="about-page" className="bg-[#0A0A0B] min-h-screen">
      <div className="relative pt-44 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/assets/hero-bars.webp" alt="Stacked gold bars in darkness" className="w-full h-full object-cover object-right opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/80 to-[#0A0A0B]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Eyebrow>Brand Narrative · By Bruno Pouliot, Founder & Principal</Eyebrow>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-wide text-white leading-tight">
            It might look like just a gold bar to you. <span className="gold-text">It is nothing short of a miracle.</span>
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 space-y-8 text-[15px] text-zinc-400 leading-relaxed" data-testid="about-story">
        <Reveal>
          <p className="text-lg text-zinc-300 border-l-2 border-[#C9A227] pl-6">
            See that gold bar that just came off assay at the refinery? Let's not forget
            what it represents. Let's not forget where it came from.
          </p>
        </Reveal>
        <Reveal>
          <p>
            Its birth started over two years ago in Africa, 5,203 feet below the surface
            of the earth, where miners gathered the raw ore used to produce it. They brought
            it up from the mine; a truck driver carried it to a refinery, where workers
            separated the silt from the raw ore. A team of chemists analyzed the grade and
            reported it — on an assay record — as being of the highest quality.
          </p>
        </Reveal>
        <Reveal>
          <p>
            It was driven from the refinery to an airport, and from there a pilot flew it to
            Dubai DBX. Airport crews removed it from the plane and shipped it onward — to
            London, to Zurich, to New York, to Hong Kong — for fulfillment based on each
            buyer's procedures. By then, the miners, the truck driver, the refinery workers,
            the chemists, the customs agent, and the pilot had all earned the wages that
            feed their families. Our sourcing operations in those countries furthered their
            journey to full development.
          </p>
        </Reveal>
        <Reveal>
          <p>
            Then the metal reached the destination refinery. It was melted again, molded to
            suit the needs of the final buyer. Technicians, engineers, machine operators,
            scientists, financiers, logistics managers — hundreds of hands brought the
            pieces of that transaction together to produce a bar that will go on to build
            high-technology components, or to protect the value of a family's work.
          </p>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="grid sm:grid-cols-3 gap-px bg-[#C9A227]/15 card-line" data-testid="about-mine-gallery">
          <Spotlight><img src="/assets/open-pit.webp" alt="Terraced open-pit mine panorama" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.04] transition-transform duration-700" /></Spotlight>
          <Spotlight><img src="/assets/mine-pit.webp" alt="Alluvial mining pit with pumps" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.04] transition-transform duration-700" /></Spotlight>
          <Spotlight><img src="/assets/mine-aerial.webp" alt="Mining camp aerial in the hills" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.04] transition-transform duration-700" /></Spotlight>
        </div>
      </div>

      <div className="bg-[#121215] hairline-t hairline-b py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>The Identity</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug">
              The <span className="gold-text">precision channel</span> for the world's minerals.
            </h2>
            <p className="mt-6 text-base text-zinc-400 leading-relaxed">
              Zorem Commodities is the macro-flow — the river that carries the full spectrum
              of earth's value. Each commodity, whether gold doré, gold bullion, copper
              cathode, or lithium, represents a fragment of the earth's original abundance.
              We absorb, structure, and regulate those flows across two groups — Precious
              Minerals & Strategic Resources, and Industrial Minerals Commodities — mirroring
              the great trading houses in both breadth and depth.
            </p>
            <blockquote className="mt-8 border-l-2 border-[#C9A227] pl-6 text-lg text-zinc-300">
              "Zorem Commodities LLC is the conduit through which the world's minerals flow,
              meticulously verified, and delivered with absolute integrity for the betterment
              of all parties involved."
            </blockquote>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <Eyebrow>Core Values</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white">
            Seven commitments. <span className="gold-text">In our own words.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A227]/15 card-line" data-testid="values-grid">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06}>
              <div data-testid={`value-card-${i}`} className="bg-[#0A0A0B] p-8 h-full hover:bg-[#16161A] transition-colors">
                <v.icon size={20} className="text-[#C9A227] mb-5" />
                <h3 className="font-display text-base text-white tracking-wide">{v.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed italic">“{v.quote}”</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <div className="bg-[#C9A227] p-8 h-full flex flex-col justify-between">
              <p className="font-display text-lg text-[#0A0A0B] leading-snug">Execution before speculation. Verified counterparties only.</p>
              <a href="/mandate" data-testid="about-values-cta" className="mt-6 inline-flex items-center justify-center px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium bg-[#0A0A0B] text-[#D4AF37] hover:bg-[#1C1C22] transition-colors">
                Submit a Mandate
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="bg-[#121215] hairline-t py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Global Footprint</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug max-w-2xl">
              One desk in Phoenix. <span className="gold-text">Corridors across four continents.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C9A227]/15 card-line" data-testid="footprint-grid">
            {FOOTPRINT.map((f) => (
              <div key={f.label} className="bg-[#0A0A0B] p-8">
                <h3 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9A227] mb-4">{f.label}</h3>
                <ul className="space-y-2 text-sm text-zinc-400">
                  {f.items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10">
            <CorridorMap />
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[#C9A227]/15 card-line">
            <Spotlight><img src="/assets/mining-truck.webp" alt="Heavy trucks on a mine haul road at dusk" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.04] transition-transform duration-700" /></Spotlight>
            <Spotlight><img src="/assets/container-loading.webp" alt="Container loading for export logistics" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.04] transition-transform duration-700" /></Spotlight>
            <Spotlight><img src="/assets/excavator-banner.webp" alt="CAT 6015 excavator on site" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.04] transition-transform duration-700" /></Spotlight>
          </div>
          <div className="mt-10 card-line bg-[#0A0A0B] p-8 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9A227] mb-4">Beyond Minerals</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                The same corridors move essential agricultural trade flows — beans, chia,
                and quinoa sourced across major producing regions — alongside energy products
                including EN590 diesel, Jet A-1, and D6 fuel oil. One network, one standard.
              </p>
            </div>
            <Spotlight>
              <img src="/assets/agro-seeds.webp" alt="Chia and quinoa seed lots from the agricultural desk" loading="lazy" className="w-full object-cover hover:scale-[1.03] transition-transform duration-700" />
            </Spotlight>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <Reveal>
          <Eyebrow>Our Mission</Eyebrow>
          <p className="font-display text-xl sm:text-2xl text-zinc-200 leading-relaxed tracking-wide">
            "To create and facilitate transparent, compliant matches between seller and buyer
            for long-term trade-flow relationships across precious mineral commodity markets —
            protecting every participant through integrity, verification, and the disciplined
            procedures of our due diligence."
          </p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <GoldButton to="/mandate" testid="about-cta-mandate">Submit a Mandate</GoldButton>
            <GoldButton to="/process" variant="ghost" testid="about-cta-process">See the Pathway</GoldButton>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default About;
